<template>
  <q-page class="">
    <div class="centered-container fill-height q-px-xl q-pt-xl q-pb-xl">
      <!-- Header -->
      <div class="row q-mt-lg items-center justify-between q-mb-md">
        <div class="col">
          <span class="text-h5">{{ i18n.title }}</span>
          <p class="text-muted q-mt-sm q-mb-none">{{ i18n.description }}</p>
        </div>
        <div class="col-shrink">
          <q-btn unelevated color="primary" :label="i18n.create_button" icon="las la-plus" no-caps @click="showCreateDialog = true" />
        </div>
      </div>

      <!-- Active API Keys -->
      <q-card v-if="activeKeys.length > 0" flat bordered class="q-mt-md">
        <q-list separator>
          <q-item v-for="key in activeKeys" :key="key.id">
            <q-item-section>
              <q-item-label>{{ key.name }}</q-item-label>
              <q-item-label caption>
                <code class="text-grey-7">{{ key.token_prefix }}...</code>
                <span class="q-ml-md">{{ i18n.created_at_label }}: {{ formatDate(key.created_at) }}</span>
                <span class="q-ml-md">{{ i18n.last_used_label }}: {{ key.last_used_at ? formatDate(key.last_used_at) : i18n.never_used }}</span>
              </q-item-label>
            </q-item-section>
            <q-item-section side>
              <q-btn flat dense color="negative" :label="i18n.revoke_button" no-caps size="sm" @click="confirmRevoke(key)" />
            </q-item-section>
          </q-item>
        </q-list>
      </q-card>

      <!-- Empty state -->
      <q-banner v-if="activeKeys.length === 0" class="bg-pastel-blue q-px-md q-mt-lg" dense>
        <template v-slot:avatar><q-icon name="las la-key" class="message-box-text" size="sm" /></template>
        <div class="text-caption message-box-text" style="font-weight: 400">{{ i18n.no_api_keys }}</div>
      </q-banner>

      <!-- Revoked keys -->
      <q-expansion-item
        v-if="revokedKeys.length > 0"
        class="q-mt-md"
        icon="las la-ban"
        :label="i18n.status_revoked + ' (' + revokedKeys.length + ')'"
        header-class="text-grey-7"
        dense
      >
        <q-card flat bordered>
          <q-list separator>
            <q-item v-for="key in revokedKeys" :key="key.id" class="text-grey-6">
              <q-item-section>
                <q-item-label class="text-grey-7">{{ key.name }}</q-item-label>
                <q-item-label caption>
                  <code>{{ key.token_prefix }}...</code>
                  <span class="q-ml-md">{{ i18n.created_at_label }}: {{ formatDate(key.created_at) }}</span>
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-badge color="negative" :label="i18n.status_revoked" />
              </q-item-section>
            </q-item>
          </q-list>
        </q-card>
      </q-expansion-item>

      <!-- ===================== DOCUMENTATION API ===================== -->
      <div class="q-mt-xl">
        <div class="text-h5 q-mb-md">{{ doc.title }}</div>
        <p class="text-muted q-mb-sm">{{ doc.intro }}</p>

        <!-- Limits banner -->
        <q-banner class="bg-pastel-blue q-mb-lg" dense rounded>
          <div class="text-subtitle2 q-mb-xs">{{ doc.limits_title }}</div>
          <ul class="q-mt-xs text-body2" style="padding-left: 20px; margin: 0;">
            <li>{{ doc.limit_workspace_type }}</li>
            <li>{{ doc.limit_one_ea }}</li>
            <li>{{ doc.limit_movements }}</li>
            <li>{{ doc.limit_categorized }}</li>
          </ul>
        </q-banner>

        <q-list bordered class="rounded-borders">
          <!-- Auth -->
          <q-expansion-item icon="las la-lock" :label="doc.auth_title" :caption="doc.auth_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.auth_text }}</p>
              <pre class="api-code">Authorization: Bearer cc_votre_cle_api</pre>
              <p class="q-mt-md">{{ doc.auth_scope }}</p>
              <div class="text-subtitle2 q-mt-md q-mb-xs">{{ doc.http_codes_title }}</div>
              <q-markup-table flat dense bordered class="q-mt-sm" style="max-width: 500px">
                <thead><tr><th class="text-left">Code</th><th class="text-left">{{ doc.description_col }}</th></tr></thead>
                <tbody>
                  <tr><td><code>200</code></td><td>{{ doc.http_ok }}</td></tr>
                  <tr><td><code>201</code></td><td>{{ doc.http_created }}</td></tr>
                  <tr><td><code>202</code></td><td>{{ doc.http_accepted }}</td></tr>
                  <tr><td><code>400</code></td><td>{{ doc.http_bad_request }}</td></tr>
                  <tr><td><code>401</code></td><td>{{ doc.http_unauthorized }}</td></tr>
                  <tr><td><code>404</code></td><td>{{ doc.http_not_found }}</td></tr>
                  <tr><td><code>403</code></td><td>{{ doc.http_forbidden }}</td></tr>
                  <tr><td><code>422</code></td><td>{{ doc.http_unprocessable }}</td></tr>
                </tbody>
              </q-markup-table>

              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.error_codes_title }}</div>
              <p class="text-caption text-muted">{{ doc.error_codes_intro }}</p>
              <q-markup-table flat dense bordered class="q-mt-sm" style="max-width: 700px">
                <thead><tr><th class="text-left">Code</th><th class="text-left">{{ doc.description_col }}</th></tr></thead>
                <tbody>
                  <tr><td><code>missing_name</code></td><td>{{ doc.error_missing_name }}</td></tr>
                  <tr><td><code>exchange_account_limit_reached</code></td><td>{{ doc.error_ea_limit }}</td></tr>
                  <tr><td><code>too_many_exchange_accounts</code></td><td>{{ doc.error_too_many_ea }}</td></tr>
                  <tr><td><code>movement_limit_exceeded</code></td><td>{{ doc.error_movement_limit }}</td></tr>
                  <tr><td><code>uncategorized_movements</code></td><td>{{ doc.error_uncategorized }}</td></tr>
                  <tr><td><code>invalid_transaction_kind</code></td><td>{{ doc.error_invalid_kind }}</td></tr>
                </tbody>
              </q-markup-table>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 1. List workspaces -->
          <q-expansion-item icon="las la-list" :label="doc.list_workspaces_title" :caption="doc.list_workspaces_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.list_workspaces_desc }}</p>
              <div class="text-subtitle2 q-mt-md q-mb-xs">{{ doc.example_label }}</div>
              <pre class="api-code">curl {{ base_url }}/api/v1/workspaces \
  -H "Authorization: Bearer cc_votre_cle_api"</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.response_label }} <code>200</code></div>
              <pre class="api-code">{
  "workspaces": [
    {
      "id": "a1b2c3d4-...",
      "name": "Jean Dupont",
      "professional": false,
      "workspace_group_id": "e5f6g7h8-...",
      "accounting_plan_id": "f6g7h8i9-...",
      "status": "created",
      "created_at": "2026-04-02T19:00:00.000Z"
    }
  ]
}</pre>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 2. Create workspace -->
          <q-expansion-item icon="las la-folder-plus" :label="doc.create_workspace_title" :caption="doc.create_workspace_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.create_workspace_desc }}</p>
              <div class="text-subtitle2 q-mb-sm">{{ doc.param_col }}</div>
              <q-markup-table flat dense bordered style="max-width: 700px">
                <thead><tr><th class="text-left">{{ doc.param_col }}</th><th class="text-left">{{ doc.type_col }}</th><th class="text-left">{{ doc.required_col }}</th><th class="text-left">{{ doc.description_col }}</th></tr></thead>
                <tbody>
                  <tr><td><code>name</code></td><td>string</td><td>{{ doc.yes_val }}</td><td>{{ doc.create_workspace_param_name }}</td></tr>
                  <tr><td><code>declaration_location_id</code></td><td>string</td><td>{{ doc.no_val }}</td><td>{{ doc.create_workspace_param_location }}</td></tr>
                </tbody>
              </q-markup-table>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.example_label }}</div>
              <pre class="api-code">curl -X POST {{ base_url }}/api/v1/workspaces \
  -H "Authorization: Bearer cc_votre_cle_api" \
  -H "Content-Type: application/json" \
  -d '{ "name": "Jean Dupont", "declaration_location_id": "75" }'</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.response_label }} <code>201</code></div>
              <pre class="api-code">{
  "id": "a1b2c3d4-...",
  "name": "Jean Dupont",
  "professional": false,
  "workspace_group_id": "e5f6g7h8-...",
  "accounting_plan_id": "f6g7h8i9-...",
  "created_at": "..."
}</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.delete_workspace_title }}</div>
              <pre class="api-code">curl -X DELETE {{ base_url }}/api/v1/workspaces/WORKSPACE_ID \
  -H "Authorization: Bearer cc_votre_cle_api"</pre>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 3. List exchange accounts -->
          <q-expansion-item icon="las la-list-ul" :label="doc.list_exchange_accounts_title" :caption="doc.list_exchange_accounts_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.list_exchange_accounts_desc }}</p>
              <div class="text-subtitle2 q-mt-md q-mb-xs">{{ doc.example_label }}</div>
              <pre class="api-code">curl {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/exchange_accounts \
  -H "Authorization: Bearer cc_votre_cle_api"</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.response_label }} <code>200</code></div>
              <pre class="api-code">{
  "exchange_accounts": [
    {
      "id": "b2c3d4e5-...",
      "name": "Binance principal",
      "exchange_name": "binance",
      "status": "done",
      "processing": false,
      "movements_count": 342,
      "crawled_at": "2026-04-03T10:30:00.000Z",
      "created_at": "2026-04-02T19:01:00.000Z"
    }
  ]
}</pre>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 3b. List methods -->
          <q-expansion-item icon="las la-puzzle-piece" :label="doc.list_methods_title" :caption="doc.list_methods_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.list_methods_desc }}</p>
              <div class="text-subtitle2 q-mt-md q-mb-xs">{{ doc.example_label }}</div>
              <pre class="api-code">curl {{ base_url }}/api/v1/exchange_methods/binance \
  -H "Authorization: Bearer cc_votre_cle_api"</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.response_label }} <code>200</code></div>
              <pre class="api-code">{
  "exchange_name": "binance",
  "methods": [
    { "id": "binance-csv", "type": "file", "label": "Binance CSV", "fields": [] },
    { "id": "comptacrypto-csv", "type": "file", "label": "CSV ComptaCrypto", "fields": [] },
    { "id": "empty", "type": "manual", "label": "Manual", "fields": [] }
  ]
}</pre>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 4. Create exchange account -->
          <q-expansion-item icon="las la-university" :label="doc.create_exchange_account_title" :caption="doc.create_exchange_account_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.create_exchange_account_desc }}</p>
              <div class="text-subtitle2 q-mb-sm">{{ doc.param_col }}</div>
              <q-markup-table flat dense bordered style="max-width: 800px">
                <thead><tr><th class="text-left">{{ doc.param_col }}</th><th class="text-left">{{ doc.type_col }}</th><th class="text-left">{{ doc.required_col }}</th><th class="text-left">{{ doc.description_col }}</th></tr></thead>
                <tbody>
                  <tr><td><code>name</code></td><td>string</td><td>{{ doc.yes_val }}</td><td>{{ doc.create_exchange_account_param_name }}</td></tr>
                  <tr><td><code>exchange_name</code></td><td>string</td><td>{{ doc.yes_val }}</td><td>{{ doc.create_exchange_account_param_exchange }}</td></tr>
                  <tr><td><code>method</code></td><td>string</td><td>{{ doc.no_val }}</td><td>{{ doc.create_exchange_account_param_method }}</td></tr>
                  <tr><td><code>api_key</code></td><td>string</td><td>{{ doc.no_val }}</td><td>{{ doc.create_exchange_account_param_api_key }}</td></tr>
                  <tr><td><code>secret_key</code></td><td>string</td><td>{{ doc.no_val }}</td><td>{{ doc.create_exchange_account_param_secret_key }}</td></tr>
                  <tr><td><code>passphrase</code></td><td>string</td><td>{{ doc.no_val }}</td><td>{{ doc.create_exchange_account_param_passphrase }}</td></tr>
                  <tr><td><code>public_address</code></td><td>string</td><td>{{ doc.no_val }}</td><td>{{ doc.create_exchange_account_param_public_address }}</td></tr>
                  <tr><td><code>file</code></td><td>file</td><td>{{ doc.no_val }}</td><td>{{ doc.create_exchange_account_param_file }}</td></tr>
                  <tr><td><code>sub_account_name</code></td><td>string</td><td>{{ doc.no_val }}</td><td>{{ doc.create_exchange_account_param_sub_account }}</td></tr>
                </tbody>
              </q-markup-table>

              <q-banner class="bg-pastel-blue q-mt-md q-mb-md" dense rounded>
                <div class="text-body2"><strong>{{ doc.create_exchange_account_modes }}</strong></div>
                <ul class="q-mt-xs text-body2" style="padding-left: 20px;">
                  <li><strong>{{ doc.create_exchange_account_mode_api }}</strong></li>
                  <li><strong>{{ doc.create_exchange_account_mode_blockchain }}</strong></li>
                  <li><strong>{{ doc.create_exchange_account_mode_file }}</strong></li>
                  <li><strong>{{ doc.create_exchange_account_mode_manual }}</strong></li>
                </ul>
              </q-banner>

              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.create_exchange_account_example_api }}</div>
              <pre class="api-code">curl -X POST {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/exchange_accounts \
  -H "Authorization: Bearer cc_votre_cle_api" \
  -H "Content-Type: application/json" \
  -d '{
    "name": "Kraken",
    "exchange_name": "kraken",
    "method": "kraken",
    "api_key": "votre_cle_api_kraken",
    "secret_key": "votre_secret_kraken"
  }'</pre>

              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.create_exchange_account_example_blockchain }}</div>
              <pre class="api-code">curl -X POST {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/exchange_accounts \
  -H "Authorization: Bearer cc_votre_cle_api" \
  -H "Content-Type: application/json" \
  -d '{
    "name": "Wallet ETH",
    "exchange_name": "ethereum",
    "method": "ethereum",
    "public_address": "0x742d35Cc..."
  }'</pre>

              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.create_exchange_account_example_csv }}</div>
              <pre class="api-code">curl -X POST {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/exchange_accounts \
  -H "Authorization: Bearer cc_votre_cle_api" \
  -F "name=Binance CSV" \
  -F "exchange_name=binance" \
  -F "method=binance-csv" \
  -F "file=@/chemin/vers/export_binance.csv"</pre>

              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.create_exchange_account_example_manual }}</div>
              <pre class="api-code">curl -X POST {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/exchange_accounts \
  -H "Authorization: Bearer cc_votre_cle_api" \
  -H "Content-Type: application/json" \
  -d '{ "name": "Compte manuel", "exchange_name": "binance" }'</pre>

              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.delete_exchange_account_title }}</div>
              <pre class="api-code">curl -X DELETE {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/exchange_accounts/ACCOUNT_ID \
  -H "Authorization: Bearer cc_votre_cle_api"</pre>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 4b. Import CSV -->
          <q-expansion-item icon="las la-file-csv" :label="doc.import_csv_title" :caption="doc.import_csv_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.import_csv_desc }}</p>
              <div class="text-subtitle2 q-mb-sm">{{ doc.param_col }}</div>
              <q-markup-table flat dense bordered style="max-width: 700px">
                <thead><tr><th class="text-left">{{ doc.param_col }}</th><th class="text-left">{{ doc.type_col }}</th><th class="text-left">{{ doc.required_col }}</th><th class="text-left">{{ doc.description_col }}</th></tr></thead>
                <tbody>
                  <tr><td><code>file</code></td><td>file</td><td>{{ doc.yes_val }}</td><td>{{ doc.import_csv_param_file }}</td></tr>
                  <tr><td><code>importer_name</code></td><td>string</td><td>{{ doc.no_val }}</td><td>{{ doc.import_csv_param_importer }}</td></tr>
                </tbody>
              </q-markup-table>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.example_label }}</div>
              <pre class="api-code">curl -X POST {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/exchange_accounts/ACCOUNT_ID/import_csv \
  -H "Authorization: Bearer cc_votre_cle_api" \
  -F "file=@/chemin/vers/export_binance.csv"</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.response_label }} <code>201</code></div>
              <pre class="api-code">{
  "id": "...",
  "exchange_account_id": "ACCOUNT_ID",
  "importer_name": "binance-csv",
  "status": "processing",
  "created_at": "..."
}</pre>
              <p class="text-caption text-muted q-mt-sm">{{ doc.import_csv_note }}</p>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 2c. Show exchange account status -->
          <q-expansion-item icon="las la-sync-alt" :label="doc.show_exchange_account_title" :caption="doc.show_exchange_account_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.show_exchange_account_desc }}</p>
              <div class="text-subtitle2 q-mb-xs">{{ doc.example_label }}</div>
              <pre class="api-code">curl {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/exchange_accounts/ACCOUNT_ID \
  -H "Authorization: Bearer cc_votre_cle_api"</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.show_exchange_account_statuses }}</div>
              <q-markup-table flat dense bordered style="max-width: 500px">
                <thead><tr><th class="text-left">Status</th><th class="text-left">{{ doc.description_col }}</th></tr></thead>
                <tbody>
                  <tr><td><code>created</code></td><td>{{ doc.show_exchange_account_status_created }}</td></tr>
                  <tr><td><code>processing</code></td><td>{{ doc.show_exchange_account_status_processing }}</td></tr>
                  <tr><td><code>done</code></td><td>{{ doc.show_exchange_account_status_done }}</td></tr>
                  <tr><td><code>processing_error</code></td><td>{{ doc.show_exchange_account_status_error }}</td></tr>
                </tbody>
              </q-markup-table>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.response_label }} <code>200</code></div>
              <pre class="api-code">{
  "id": "b2c3d4e5-...",
  "name": "Binance principal",
  "exchange_name": "binance",
  "workspace_id": "a1b2c3d4-...",
  "status": "done",
  "processing": false,
  "crawled_at": "2026-04-03T10:30:00.000Z",
  "crawled_percentage": 100,
  "movements_count": 342,
  "etl_errors": null,
  "created_at": "...",
  "updated_at": "..."
}</pre>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 5. List exchange assets -->
          <q-expansion-item icon="las la-coins" :label="doc.list_exchange_assets_title" :caption="doc.list_exchange_assets_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.list_exchange_assets_desc }}</p>
              <div class="text-subtitle2 q-mt-md q-mb-xs">{{ doc.example_label }}</div>
              <pre class="api-code">curl {{ base_url }}/api/v1/exchange_assets/binance \
  -H "Authorization: Bearer cc_votre_cle_api"</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.response_label }} <code>200</code></div>
              <pre class="api-code">{
  "exchange_assets": [
    {
      "id": "...",
      "remote_id": "ETH",
      "name": "Ethereum",
      "exchange_symbol": "ETH",
      "asset_symbol": "ETH",
      "logo_url": "...",
      "coinmarketcap_url": "https://coinmarketcap.com/currencies/ethereum/",
      "coingecko_url": "https://www.coingecko.com/en/coins/ethereum"
    }
  ]
}</pre>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 6. List transaction kinds -->
          <q-expansion-item icon="las la-tags" :label="doc.list_kinds_title" :caption="doc.list_kinds_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.list_kinds_desc }}</p>
              <div class="text-subtitle2 q-mt-md q-mb-xs">{{ doc.example_label }}</div>
              <pre class="api-code">curl {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/transaction_kinds \
  -H "Authorization: Bearer cc_votre_cle_api"</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.response_label }} <code>200</code></div>
              <pre class="api-code">{
  "transaction_kinds": [
    { "id": "aaa-bbb-...", "name": "trade_in", "label_fr": "Achat", "description": "...", "category": "..." },
    { "id": "ccc-ddd-...", "name": "trade_out", "label_fr": "Vente", "description": "...", "category": "..." },
    { "id": "eee-fff-...", "name": "commission", "label_fr": "Commission", "description": "...", "category": "..." }
  ]
}</pre>
              <p class="text-caption text-muted q-mt-sm">{{ doc.list_kinds_note }}</p>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 4. Create movement -->
          <q-expansion-item icon="las la-exchange-alt" :label="doc.create_movement_title" :caption="doc.create_movement_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.create_movement_desc }}</p>
              <div class="text-subtitle2 q-mb-sm">{{ doc.param_col }}</div>
              <q-markup-table flat dense bordered style="max-width: 800px">
                <thead><tr><th class="text-left">{{ doc.param_col }}</th><th class="text-left">{{ doc.type_col }}</th><th class="text-left">{{ doc.required_col }}</th><th class="text-left">{{ doc.description_col }}</th></tr></thead>
                <tbody>
                  <tr><td><code>id</code></td><td>uuid</td><td>{{ doc.no_val }}</td><td>{{ doc.create_movement_param_id }}</td></tr>
                  <tr><td><code>amount</code></td><td>decimal</td><td>{{ doc.yes_val }}</td><td>{{ doc.create_movement_param_amount }}</td></tr>
                  <tr><td><code>time</code></td><td>datetime</td><td>{{ doc.yes_val }}</td><td>{{ doc.create_movement_param_time }}</td></tr>
                  <tr><td><code>local_identifier</code></td><td>string</td><td>{{ doc.yes_val }}</td><td>{{ doc.create_movement_param_local_id }}</td></tr>
                  <tr><td><code>exchange_asset_remote_id</code></td><td>string</td><td>{{ doc.yes_val }}*</td><td>{{ doc.create_movement_param_asset_remote }}</td></tr>
                  <tr><td><code>exchange_asset_id</code></td><td>uuid</td><td>{{ doc.yes_val }}*</td><td>{{ doc.create_movement_param_asset_id }}</td></tr>
                  <tr><td><code>transaction_kind_id</code></td><td>uuid</td><td>{{ doc.no_val }}</td><td>{{ doc.create_movement_param_kind }}</td></tr>
                  <tr><td><code>trade_in_id</code></td><td>uuid</td><td>{{ doc.no_val }}</td><td>{{ doc.create_movement_param_trade_in }}</td></tr>
                  <tr><td><code>fiat_amount</code></td><td>decimal</td><td>{{ doc.no_val }}</td><td>{{ doc.create_movement_param_fiat }}</td></tr>
                  <tr><td><code>from_address</code></td><td>string</td><td>{{ doc.no_val }}</td><td>{{ doc.create_movement_param_from }}</td></tr>
                  <tr><td><code>to_address</code></td><td>string</td><td>{{ doc.no_val }}</td><td>{{ doc.create_movement_param_to }}</td></tr>
                  <tr><td><code>blockchain_txid</code></td><td>string</td><td>{{ doc.no_val }}</td><td>{{ doc.create_movement_param_txid }}</td></tr>
                  <tr><td><code>info</code></td><td>string</td><td>{{ doc.no_val }}</td><td>{{ doc.create_movement_param_info }}</td></tr>
                  <tr><td><code>note</code></td><td>string</td><td>{{ doc.no_val }}</td><td>{{ doc.create_movement_param_note }}</td></tr>
                </tbody>
              </q-markup-table>
              <p class="text-caption text-muted q-mt-sm">* {{ doc.create_movement_asset_note }}</p>

              <q-banner class="bg-pastel-blue q-mt-lg q-mb-md" dense rounded>
                <div class="text-subtitle2 q-mb-xs">{{ doc.trades_title }}</div>
                <div class="text-body2">{{ doc.trades_desc }}</div>
                <ol class="q-mt-sm q-mb-sm text-body2" style="padding-left: 20px;">
                  <li>{{ doc.trades_step1 }}</li>
                  <li>{{ doc.trades_step2 }}</li>
                </ol>
                <div class="text-body2"><strong>{{ doc.trades_rules_title }}</strong></div>
                <ul class="q-mt-xs text-body2" style="padding-left: 20px;">
                  <li>{{ doc.trades_rule1 }}</li>
                  <li>{{ doc.trades_rule2 }}</li>
                  <li>{{ doc.trades_rule3 }}</li>
                  <li>{{ doc.trades_rule4 }}</li>
                </ul>
              </q-banner>

              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.example_deposit }}</div>
              <pre class="api-code">curl -X POST {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/exchange_accounts/ACCOUNT_ID/movements \
  -H "Authorization: Bearer cc_votre_cle_api" \
  -H "Content-Type: application/json" \
  -d '{
    "amount": 0.5,
    "time": "2025-03-15T14:30:00Z",
    "local_identifier": "deposit-001",
    "exchange_asset_remote_id": "BTC",
    "fiat_amount": 15000.00,
    "transaction_kind_id": "TRANSACTION_KIND_ID"
  }'</pre>

              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.example_trade }}</div>
              <p class="text-caption text-muted">{{ doc.example_trade_note }}</p>
              <pre class="api-code"># {{ doc.example_trade_in_comment }}
curl -X POST {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/exchange_accounts/ACCOUNT_ID/movements \
  -H "Authorization: Bearer cc_votre_cle_api" \
  -H "Content-Type: application/json" \
  -d '{
    "id": "11111111-1111-1111-1111-111111111111",
    "amount": 0.5,
    "time": "2025-03-15T14:30:00Z",
    "local_identifier": "trade-001-in",
    "exchange_asset_remote_id": "BTC",
    "transaction_kind_id": "TRADE_IN_KIND_ID"
  }'</pre>
              <pre class="api-code q-mt-sm"># {{ doc.example_trade_out_comment }}
curl -X POST {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/exchange_accounts/ACCOUNT_ID/movements \
  -H "Authorization: Bearer cc_votre_cle_api" \
  -H "Content-Type: application/json" \
  -d '{
    "amount": -15000.00,
    "time": "2025-03-15T14:30:00Z",
    "local_identifier": "trade-001-out",
    "exchange_asset_remote_id": "EUR",
    "transaction_kind_id": "TRADE_OUT_KIND_ID",
    "trade_in_id": "11111111-1111-1111-1111-111111111111"
  }'</pre>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 8. Categorize -->
          <q-expansion-item icon="las la-tag" :label="doc.categorize_title" :caption="doc.categorize_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.categorize_desc }}</p>
              <div class="text-subtitle2 q-mb-sm">{{ doc.param_col }}</div>
              <q-markup-table flat dense bordered style="max-width: 700px">
                <thead><tr><th class="text-left">{{ doc.param_col }}</th><th class="text-left">{{ doc.type_col }}</th><th class="text-left">{{ doc.required_col }}</th><th class="text-left">{{ doc.description_col }}</th></tr></thead>
                <tbody>
                  <tr><td><code>transaction_kind_id</code></td><td>uuid</td><td>{{ doc.yes_val }}</td><td>{{ doc.categorize_param_kind }}</td></tr>
                </tbody>
              </q-markup-table>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.example_label }}</div>
              <pre class="api-code">curl -X PATCH {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/exchange_accounts/ACCOUNT_ID/movements/MOVEMENT_ID/categorize \
  -H "Authorization: Bearer cc_votre_cle_api" \
  -H "Content-Type: application/json" \
  -d '{ "transaction_kind_id": "TRANSACTION_KIND_ID" }'</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.response_label }} <code>200</code></div>
              <pre class="api-code">{ "id": "MOVEMENT_ID", "transaction_kind_id": "...", "justified": true }</pre>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 9. Bulk -->
          <q-expansion-item icon="las la-layer-group" :label="doc.bulk_title" :caption="doc.bulk_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.bulk_desc }}</p>
              <div class="text-subtitle2 q-mb-sm">{{ doc.param_col }}</div>
              <q-markup-table flat dense bordered style="max-width: 700px">
                <thead><tr><th class="text-left">{{ doc.param_col }}</th><th class="text-left">{{ doc.type_col }}</th><th class="text-left">{{ doc.required_col }}</th><th class="text-left">{{ doc.description_col }}</th></tr></thead>
                <tbody>
                  <tr><td><code>movements</code></td><td>array</td><td>{{ doc.yes_val }}</td><td>{{ doc.bulk_param_movements }}</td></tr>
                </tbody>
              </q-markup-table>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.example_label }}</div>
              <pre class="api-code">curl -X POST {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/exchange_accounts/ACCOUNT_ID/movements/bulk \
  -H "Authorization: Bearer cc_votre_cle_api" \
  -H "Content-Type: application/json" \
  -d '{
    "movements": [
      {
        "id": "aaaa-bbbb-cccc-dddd",
        "amount": 1.0,
        "time": "2025-01-15T10:00:00Z",
        "local_identifier": "trade-001-in",
        "exchange_asset_remote_id": "BTC",
        "fiat_amount": 40000.00,
        "transaction_kind_id": "TRADE_IN_KIND_ID"
      },
      {
        "amount": -40000.00,
        "time": "2025-01-15T10:00:00Z",
        "local_identifier": "trade-001-out",
        "exchange_asset_remote_id": "EUR",
        "transaction_kind_id": "TRADE_OUT_KIND_ID",
        "trade_in_id": "aaaa-bbbb-cccc-dddd"
      }
    ]
  }'</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.response_label }} <code>201</code></div>
              <pre class="api-code">{
  "created_count": 2,
  "movements": [
    { "id": "aaaa-bbbb-cccc-dddd", "local_identifier": "trade-001-in" },
    { "id": "...", "local_identifier": "trade-001-out" }
  ]
}</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.bulk_error_response }} <code>422</code></div>
              <pre class="api-code">{
  "error": "Bulk import failed",
  "details": [{ "index": 1, "local_identifier": "trade-001-out", "errors": ["..."] }]
}</pre>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- Delete movements -->
          <q-expansion-item icon="las la-trash" :label="doc.delete_movement_title" :caption="doc.delete_movement_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.delete_movement_desc }}</p>
              <div class="text-subtitle2 q-mt-md q-mb-xs">{{ doc.example_label }}</div>
              <pre class="api-code"># Single
curl -X DELETE {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/exchange_accounts/ACCOUNT_ID/movements/MOVEMENT_ID \
  -H "Authorization: Bearer cc_votre_cle_api"

# Bulk
curl -X POST {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/exchange_accounts/ACCOUNT_ID/movements/bulk_destroy \
  -H "Authorization: Bearer cc_votre_cle_api" \
  -H "Content-Type: application/json" \
  -d '{ "ids": ["MOVEMENT_ID_1", "MOVEMENT_ID_2"] }'</pre>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 10. Uncategorized movements -->
          <q-expansion-item icon="las la-question-circle" :label="doc.uncategorized_title" :caption="doc.uncategorized_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.uncategorized_desc }}</p>
              <div class="text-subtitle2 q-mb-sm">{{ doc.param_col }}</div>
              <q-markup-table flat dense bordered style="max-width: 700px">
                <thead><tr><th class="text-left">{{ doc.param_col }}</th><th class="text-left">{{ doc.type_col }}</th><th class="text-left">{{ doc.required_col }}</th><th class="text-left">{{ doc.description_col }}</th></tr></thead>
                <tbody>
                  <tr><td><code>limit</code></td><td>integer</td><td>{{ doc.no_val }}</td><td>{{ doc.uncategorized_param_limit }}</td></tr>
                  <tr><td><code>offset</code></td><td>integer</td><td>{{ doc.no_val }}</td><td>{{ doc.uncategorized_param_offset }}</td></tr>
                </tbody>
              </q-markup-table>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.example_label }}</div>
              <pre class="api-code">curl {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/uncategorized_movements \
  -H "Authorization: Bearer cc_votre_cle_api"</pre>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 11. Swap -->
          <q-expansion-item icon="las la-sync" :label="doc.swap_title" :caption="doc.swap_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.swap_desc }}</p>
              <div class="text-subtitle2 q-mb-sm">{{ doc.param_col }}</div>
              <q-markup-table flat dense bordered style="max-width: 700px">
                <thead><tr><th class="text-left">{{ doc.param_col }}</th><th class="text-left">{{ doc.type_col }}</th><th class="text-left">{{ doc.required_col }}</th><th class="text-left">{{ doc.description_col }}</th></tr></thead>
                <tbody>
                  <tr><td><code>movements</code></td><td>array</td><td>{{ doc.yes_val }}</td><td>{{ doc.swap_param_movements }}</td></tr>
                </tbody>
              </q-markup-table>
              <q-banner class="bg-pastel-blue q-mt-md q-mb-md" dense rounded>
                <div class="text-body2"><strong>{{ doc.swap_conditions_title }}</strong></div>
                <ul class="q-mt-xs text-body2" style="padding-left: 20px;">
                  <li>{{ doc.swap_condition1 }}</li>
                  <li>{{ doc.swap_condition2 }}</li>
                  <li>{{ doc.swap_condition3 }}</li>
                </ul>
              </q-banner>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.example_label }}</div>
              <pre class="api-code">curl -X POST {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/swap_movements \
  -H "Authorization: Bearer cc_votre_cle_api" \
  -H "Content-Type: application/json" \
  -d '{
    "movements": ["ID_POSITIVE", "ID_NEGATIVE"]
  }'</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.response_label }} <code>200</code></div>
              <pre class="api-code">{
  "trade_in": { "id": "ID_POSITIVE", "transaction_kind_id": "TRADE_IN_KIND_ID" },
  "trade_out": { "id": "ID_NEGATIVE", "transaction_kind_id": "TRADE_OUT_KIND_ID", "trade_in_id": "ID_POSITIVE" }
}</pre>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 12. Generate report -->
          <q-expansion-item icon="las la-file-invoice" :label="doc.generate_report_title" :caption="doc.generate_report_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.generate_report_desc }}</p>
              <q-banner class="bg-pastel-blue q-mb-md" dense rounded>
                <div class="text-body2"><strong>{{ doc.generate_report_conditions }}</strong></div>
                <ul class="q-mt-xs text-body2" style="padding-left: 20px;">
                  <li>{{ doc.generate_report_condition1 }}</li>
                  <li>{{ doc.generate_report_condition2 }}</li>
                  <li>{{ doc.generate_report_condition3 }}</li>
                </ul>
              </q-banner>
              <div class="text-subtitle2 q-mb-sm">{{ doc.param_col }}</div>
              <q-markup-table flat dense bordered style="max-width: 700px">
                <thead><tr><th class="text-left">{{ doc.param_col }}</th><th class="text-left">{{ doc.type_col }}</th><th class="text-left">{{ doc.required_col }}</th><th class="text-left">{{ doc.description_col }}</th></tr></thead>
                <tbody>
                  <tr><td><code>year</code></td><td>integer</td><td>{{ doc.no_val }}</td><td>{{ doc.generate_report_param_year }}</td></tr>
                </tbody>
              </q-markup-table>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.example_label }}</div>
              <pre class="api-code">curl -X POST {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/casual_reports \
  -H "Authorization: Bearer cc_votre_cle_api" \
  -H "Content-Type: application/json" \
  -d '{ "year": 2025 }'</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.response_label }} <code>202</code></div>
              <pre class="api-code">{ "id": "e5f6g7h8-...", "status": "processing", "year": 2025, "created_at": "..." }</pre>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 8. Report status -->
          <q-expansion-item icon="las la-tasks" :label="doc.report_status_title" :caption="doc.report_status_caption" header-class="text-bold">
            <q-card><q-card-section>
              <div class="text-subtitle2 q-mb-xs">{{ doc.example_label }}</div>
              <pre class="api-code">curl {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/casual_reports/REPORT_ID \
  -H "Authorization: Bearer cc_votre_cle_api"</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.report_status_label }}</div>
              <q-markup-table flat dense bordered style="max-width: 500px">
                <thead><tr><th class="text-left">Status</th><th class="text-left">{{ doc.description_col }}</th></tr></thead>
                <tbody>
                  <tr><td><code>processing</code></td><td>{{ doc.report_status_processing }}</td></tr>
                  <tr><td><code>success</code></td><td>{{ doc.report_status_success }}</td></tr>
                  <tr><td><code>error</code></td><td>{{ doc.report_status_error }}</td></tr>
                </tbody>
              </q-markup-table>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.report_status_response_done }}</div>
              <pre class="api-code">{
  "id": "...", "status": "success", "year": 2025,
  "data": {
    "bic": 0, "bnc": 0, "net_total_buy_price": 15000.0,
    "total_tax": 900.0, "total_taxable_pnl": 3000.0,
    "total_taxable_transactions": 18000.0, "total_wallet_value": 0.0
  },
  "created_at": "...", "updated_at": "..."
}</pre>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- 9. Download files -->
          <q-expansion-item icon="las la-download" :label="doc.download_files_title" :caption="doc.download_files_caption" header-class="text-bold">
            <q-card><q-card-section>
              <p>{{ doc.download_files_desc }}</p>
              <div class="text-subtitle2 q-mt-md q-mb-xs">{{ doc.download_certificate }}</div>
              <pre class="api-code">curl -o attestation.pdf \
  {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/casual_reports/REPORT_ID/certificate_pdf \
  -H "Authorization: Bearer cc_votre_cle_api"</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.download_fiscal_report }}</div>
              <pre class="api-code">curl -o bilan-activite.pdf \
  {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/casual_reports/REPORT_ID/fiscal_report_pdf \
  -H "Authorization: Bearer cc_votre_cle_api"</pre>
              <div class="text-subtitle2 q-mt-lg q-mb-xs">{{ doc.download_sales_xlsx }}</div>
              <pre class="api-code">curl -o annexe2086.xlsx \
  {{ base_url }}/api/v1/workspaces/WORKSPACE_ID/casual_reports/REPORT_ID/sales_xlsx \
  -H "Authorization: Bearer cc_votre_cle_api"</pre>
              <p class="q-mt-md text-muted text-caption">{{ doc.download_not_ready }}</p>
            </q-card-section></q-card>
          </q-expansion-item>
          <q-separator />

          <!-- Full example -->
          <q-expansion-item icon="las la-terminal" :label="doc.full_example_title" :caption="doc.full_example_caption" header-class="text-bold">
            <q-card><q-card-section>
              <pre class="api-code">#!/bin/bash
set -e
API_KEY="cc_votre_cle_api"
BASE="{{ base_url }}/api/v1"
json_val() { ruby -e "require 'json'; puts JSON.parse(STDIN.read)\$1"; }

# 1. Workspace
W=$(curl -s -X POST "$BASE/workspaces" \
  -H "Authorization: Bearer $API_KEY" \
  -H "Content-Type: application/json" \
  -d '{ "name": "Exemple Particulier", "declaration_location_id": "75" }')
WID=$(echo "$W" | json_val '["id"]')

# 2. Exchange account Binance (vide)
EA=$(curl -s -X POST "$BASE/workspaces/$WID/exchange_accounts" \
  -H "Authorization: Bearer $API_KEY" \
  -H "Content-Type: application/json" \
  -d '{ "name": "Binance", "exchange_name": "binance" }')
EAID=$(echo "$EA" | json_val '["id"]')

# 3. Transaction kinds
K=$(curl -s "$BASE/workspaces/$WID/transaction_kinds" -H "Authorization: Bearer $API_KEY")
TI=$(echo "$K" | ruby -e 'require "json"; puts JSON.parse(STDIN.read)["transaction_kinds"].find{|x|x["name"]=="trade_in"}["id"]')
TO=$(echo "$K" | ruby -e 'require "json"; puts JSON.parse(STDIN.read)["transaction_kinds"].find{|x|x["name"]=="trade_out"}["id"]')
IT=$(echo "$K" | ruby -e 'require "json"; puts JSON.parse(STDIN.read)["transaction_kinds"].find{|x|x["name"]=="internal_transfer"}["id"]')
CO=$(echo "$K" | ruby -e 'require "json"; puts JSON.parse(STDIN.read)["transaction_kinds"].find{|x|x["name"]=="commission"}["id"]')
AD=$(echo "$K" | ruby -e 'require "json"; puts JSON.parse(STDIN.read)["transaction_kinds"].find{|x|x["name"]=="free_crypto_earning"}["id"]')

# 4. Mouvements
# - 10000 EUR internal_transfer
# - swap 10000 EUR -&gt; 1 BTC
# - swap 1 BTC -&gt; 5 ETH
# - 0.1 ETH commission
# - swap 4.9 ETH -&gt; 1.1 BTC
# - swap 1.1 BTC -&gt; 7 ETH
# - 0.1 ETH airdrop
# - vente 6 ETH -&gt; 12560 EUR
# - 10000 EUR internal_transfer sortant
curl -s -X POST "$BASE/workspaces/$WID/exchange_accounts/$EAID/movements/bulk" \
  -H "Authorization: Bearer $API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
  "movements": [
    { "amount": 10000, "time": "2023-01-15T10:00:00Z",
      "local_identifier": "deposit-eur", "exchange_asset_remote_id": "EUR",
      "transaction_kind_id": "'$IT'" },

    { "id": "00000001-0001-0001-0001-000000000001",
      "amount": 1.0, "time": "2023-03-01T14:00:00Z",
      "local_identifier": "swap1-btc-in", "exchange_asset_remote_id": "BTC",
      "transaction_kind_id": "'$TI'" },
    { "amount": -10000, "time": "2023-03-01T14:00:00Z",
      "local_identifier": "swap1-eur-out", "exchange_asset_remote_id": "EUR",
      "transaction_kind_id": "'$TO'",
      "trade_in_id": "00000001-0001-0001-0001-000000000001" },

    { "id": "00000002-0002-0002-0002-000000000002",
      "amount": 5.0, "time": "2023-04-15T11:00:00Z",
      "local_identifier": "swap2-eth-in", "exchange_asset_remote_id": "ETH",
      "transaction_kind_id": "'$TI'" },
    { "amount": -1.0, "time": "2023-04-15T11:00:00Z",
      "local_identifier": "swap2-btc-out", "exchange_asset_remote_id": "BTC",
      "transaction_kind_id": "'$TO'",
      "trade_in_id": "00000002-0002-0002-0002-000000000002" },

    { "amount": -0.1, "time": "2023-05-20T09:00:00Z",
      "local_identifier": "fee-eth", "exchange_asset_remote_id": "ETH",
      "transaction_kind_id": "'$CO'" },

    { "id": "00000003-0003-0003-0003-000000000003",
      "amount": 1.1, "time": "2023-06-10T16:00:00Z",
      "local_identifier": "swap3-btc-in", "exchange_asset_remote_id": "BTC",
      "transaction_kind_id": "'$TI'" },
    { "amount": -4.9, "time": "2023-06-10T16:00:00Z",
      "local_identifier": "swap3-eth-out", "exchange_asset_remote_id": "ETH",
      "transaction_kind_id": "'$TO'",
      "trade_in_id": "00000003-0003-0003-0003-000000000003" },

    { "id": "00000004-0004-0004-0004-000000000004",
      "amount": 7.0, "time": "2023-07-25T13:00:00Z",
      "local_identifier": "swap4-eth-in", "exchange_asset_remote_id": "ETH",
      "transaction_kind_id": "'$TI'" },
    { "amount": -1.1, "time": "2023-07-25T13:00:00Z",
      "local_identifier": "swap4-btc-out", "exchange_asset_remote_id": "BTC",
      "transaction_kind_id": "'$TO'",
      "trade_in_id": "00000004-0004-0004-0004-000000000004" },

    { "amount": 0.1, "time": "2023-08-15T10:00:00Z",
      "local_identifier": "airdrop-eth", "exchange_asset_remote_id": "ETH",
      "transaction_kind_id": "'$AD'" },

    { "id": "00000005-0005-0005-0005-000000000005",
      "amount": 12560, "time": "2023-10-01T15:00:00Z",
      "local_identifier": "sell-eur-in", "exchange_asset_remote_id": "EUR",
      "transaction_kind_id": "'$TI'" },
    { "amount": -6.0, "time": "2023-10-01T15:00:00Z",
      "local_identifier": "sell-eth-out", "exchange_asset_remote_id": "ETH",
      "transaction_kind_id": "'$TO'",
      "trade_in_id": "00000005-0005-0005-0005-000000000005" },

    { "amount": -10000, "time": "2023-11-20T12:00:00Z",
      "local_identifier": "withdraw-eur", "exchange_asset_remote_id": "EUR",
      "transaction_kind_id": "'$IT'" }
  ]
}'

# 5. Rapport 2023
R=$(curl -s -X POST "$BASE/workspaces/$WID/casual_reports" \
  -H "Authorization: Bearer $API_KEY" \
  -H "Content-Type: application/json" \
  -d '{ "year": 2023 }')
RID=$(echo "$R" | json_val '["id"]')

# 6. Attente
while true; do
  S=$(echo $(curl -s "$BASE/workspaces/$WID/casual_reports/$RID" \
    -H "Authorization: Bearer $API_KEY") | json_val '["status"]')
  echo "Statut: $S"
  [ "$S" = "success" ] &amp;&amp; break
  [ "$S" = "error" ] &amp;&amp; exit 1
  sleep 5
done

# 7. Fichiers
curl -o attestation.pdf "$BASE/workspaces/$WID/casual_reports/$RID/certificate_pdf" \
  -H "Authorization: Bearer $API_KEY"
curl -o bilan.pdf "$BASE/workspaces/$WID/casual_reports/$RID/fiscal_report_pdf" \
  -H "Authorization: Bearer $API_KEY"
curl -o annexe2086.xlsx "$BASE/workspaces/$WID/casual_reports/$RID/sales_xlsx" \
  -H "Authorization: Bearer $API_KEY"
echo "OK"</pre>
            </q-card-section></q-card>
          </q-expansion-item>
        </q-list>
      </div>

      <!-- ===================== DIALOGS ===================== -->
      <q-dialog v-model="showCreateDialog" persistent>
        <q-card style="min-width: 400px">
          <q-card-section><div class="text-h6">{{ i18n.create_button }}</div></q-card-section>
          <q-card-section>
            <q-input v-model="newKeyName" outlined dense :label="i18n.name_label" :placeholder="i18n.name_placeholder" />
          </q-card-section>
          <q-card-actions align="right">
            <q-btn flat :label="i18n.revoke_confirm_cancel" no-caps v-close-popup />
            <q-btn unelevated color="primary" :label="i18n.create_button" no-caps :loading="creating" :disable="!newKeyName" @click="createKey" />
          </q-card-actions>
        </q-card>
      </q-dialog>

      <q-dialog v-model="showTokenDialog" persistent>
        <q-card style="min-width: 500px">
          <q-card-section><div class="text-h6">{{ i18n.token_dialog_title }}</div></q-card-section>
          <q-card-section>
            <q-banner class="bg-warning q-mb-md" dense rounded>
              <div class="text-caption text-bold">{{ i18n.token_dialog_message }}</div>
            </q-banner>
            <div class="row items-center q-pa-sm rounded-borders" style="background: #f5f5f5; border: 1px solid #e0e0e0; cursor: pointer;" @click="copyToken">
              <code class="col text-body2 q-px-sm" style="word-break: break-all; user-select: all;">{{ createdToken }}</code>
              <q-btn flat dense round :icon="copied ? 'las la-check' : 'las la-copy'" :color="copied ? 'positive' : 'grey-7'" size="sm" @click.stop="copyToken" />
            </div>
            <div v-if="copied" class="text-positive text-caption q-mt-xs">{{ i18n.copied }}</div>
          </q-card-section>
          <q-card-actions align="right">
            <q-btn unelevated color="primary" :label="i18n.token_dialog_close" no-caps v-close-popup />
          </q-card-actions>
        </q-card>
      </q-dialog>

      <q-dialog v-model="showRevokeDialog">
        <q-card style="min-width: 350px">
          <q-card-section><div class="text-h6">{{ i18n.revoke_confirm_title }}</div></q-card-section>
          <q-card-section>{{ i18n.revoke_confirm_message }}</q-card-section>
          <q-card-actions align="right">
            <q-btn flat :label="i18n.revoke_confirm_cancel" no-caps v-close-popup />
            <q-btn unelevated color="negative" :label="i18n.revoke_confirm_ok" no-caps @click="revokeKey" />
          </q-card-actions>
        </q-card>
      </q-dialog>
    </div>
  </q-page>
</template>

<script>
import Layout from '@/layouts/wg_admin.vue';
import { router } from '@inertiajs/vue3';

export default {
  layout: Layout,
  props: {
    i18n: Object,
    api_keys: Array,
    create_action: String,
  },
  data() {
    return {
      showCreateDialog: false,
      showTokenDialog: false,
      showRevokeDialog: false,
      newKeyName: '',
      createdToken: '',
      creating: false,
      copied: false,
      keyToRevoke: null,
    };
  },
  computed: {
    activeKeys() {
      return this.api_keys.filter(k => k.active);
    },
    revokedKeys() {
      return this.api_keys.filter(k => !k.active);
    },
    base_url() {
      return window.location.origin;
    },
    doc() {
      return this.i18n.doc;
    },
  },
  mounted() {
    this.checkFlashToken();
  },
  watch: {
    '$page.props.flash'() {
      this.checkFlashToken();
    },
  },
  methods: {
    checkFlashToken() {
      const token = this.$page.props.flash?.api_token;
      if (token) {
        this.createdToken = token;
        this.showTokenDialog = true;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString(undefined, {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    },
    createKey() {
      this.creating = true;
      router.post(this.create_action, { name: this.newKeyName }, {
        onFinish: () => { this.creating = false; this.showCreateDialog = false; this.newKeyName = ''; },
      });
    },
    confirmRevoke(key) {
      this.keyToRevoke = key;
      this.showRevokeDialog = true;
    },
    revokeKey() {
      if (!this.keyToRevoke) return;
      router.delete(`${this.create_action}/${this.keyToRevoke.id}`, {
        onFinish: () => { this.showRevokeDialog = false; this.keyToRevoke = null; },
      });
    },
    copyToken() {
      navigator.clipboard.writeText(this.createdToken);
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 3000);
    },
  },
};
</script>

<style scoped>
.api-code {
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.5;
  overflow-x: auto;
  white-space: pre;
  margin: 0;
}
</style>
